import { Switch } from '@/components/ui/switch'
import {
  useUserPreferences,
  useUpdateUserPreferences,
  useSetAnalyticsObjection,
} from '@/hooks/queries'
import { useAuth } from '@/contexts/AuthContext'
import { setAnalyticsSuppressed } from '@intelhub/analytics/client'
import {
  stopReplayRecordingNow,
  confirmReplayRecordingStop,
  releaseReplayRecordingStop,
  markReplayWithdrawal,
  releaseConfirmedReplayStops,
} from '@/lib/analytics'

/**
 * Settings — Privacy (KAN-32 A.9, R12 / decision O3).
 *
 * The session-replay objection. Replay runs under legitimate interest, and
 * GDPR Art. 21(4) makes the right to object non-optional — so this is a
 * privacy control, not a preference. It is therefore:
 *
 *  - driven only by the server's answer (no client-side default, no
 *    "recommended" badge),
 *  - frictionless in both directions (no confirmation dialog),
 *  - plainly worded about what is captured and what is masked.
 *
 * Polarity (KAN-904 fix, controller decision F51): each switch shows the state
 * of the thing its label names. ON means recording/collection is happening;
 * turning it OFF records the objection. The persisted flags keep their meaning
 * (`replayOptOut`, `analytics_suppressed_at` = "the member objected"), so the
 * switch is their inverse. The earlier version bound `checked` to the
 * objection, which showed OFF while recording ran under copy saying "Turn this
 * off" — a misleading objection control. The state line under the copy spells
 * the state out so the control cannot be misread.
 *
 * The value is persisted server-side rather than in the browser because it
 * must ride on `GET /api/auth/me` (A.8) — the client can then never start
 * recording before it knows the answer.
 *
 * Ordering (KAN-904, codex pol-ddfc621d) matches the analytics switch:
 *  - objecting (switching OFF) stops the recorder in THIS tab before the
 *    request leaves (`stopReplayRecordingNow`), and the in-tab stop holds until
 *    the server state takes over, so nothing is recorded during the round-trip
 *    or on a navigation while the save is in flight;
 *  - withdrawing (switching ON) never resumes on the client's say-so: the stop
 *    is released only once the save succeeded, and recording restarts only
 *    when the refreshed `/me` says it may;
 *  - `/me` is re-read on SETTLED. After a failed save the recorder and the
 *    switch both follow what the server holds, and the member is told.
 * No browser storage is written in either direction.
 *
 * Concurrent saves (KAN-904, codex pol-fix-673c930a): until `/preferences`
 * refreshes the switch still reads ON, so a second click sends a second
 * objection. Each OFF takes its own stop hold, and each save settles its OWN
 * hold, so one objection failing never lifts the stop another still needs.
 * That is why this uses `mutateAsync` and per-call promise handlers: TanStack
 * Query fires callbacks passed to `mutate()` only for the LAST call, so the
 * earlier save's success or failure would never reach its hold.
 *
 * KAN-904 adds the product-analytics objection (board minute condition 4)
 * under the same rules. See `AnalyticsObjection` below.
 */
export function PrivacySection() {
  const { user, refreshUser } = useAuth()
  const userId = user?.sub ?? ''
  const { data: preferences } = useUserPreferences(userId)
  const updatePreferences = useUpdateUserPreferences()

  // No optimistic default: absent server state reads as "not objected", which
  // is what the NOT NULL DEFAULT FALSE column means.
  const objected = preferences?.replayOptOut ?? false

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium">Session recording</p>
            <p className="text-xs text-muted-foreground">
              We record anonymised playback of product pages to improve the
              interface. Inputs and on-screen text are masked before anything
              leaves your browser, and the admin console is never recorded.
            </p>
            <p className="text-xs text-muted-foreground">
              Turn this off whenever you want. Recording stops immediately and
              nothing further is captured.
            </p>
          </div>
          <Switch
            // ON = recording is active. OFF = the member has objected.
            checked={!objected}
            onCheckedChange={(recordingOn) => {
              const objecting = !recordingOn
              // Stop first: the objection bites at the click, not after the
              // round-trip. The hold belongs to THIS save only.
              const hold = objecting ? stopReplayRecordingNow() : null
              const withdrawalMark = objecting ? null : markReplayWithdrawal()
              // Refresh the auth context on settle: /me is what the replay
              // policy reads, and it is fetched once on mount. Without this the
              // objection does not take effect until a page reload — and an
              // Art. 21 objection honoured only after reload is not honoured.
              updatePreferences
                .mutateAsync({ userId, replayOptOut: objecting })
                .then(
                  () => {
                    if (hold !== null) confirmReplayRecordingStop(hold)
                    if (withdrawalMark !== null) releaseConfirmedReplayStops(withdrawalMark)
                  },
                  () => {
                    // Shown via `isError`; a failed objection releases its own
                    // hold only, a failed withdrawal releases nothing.
                    if (hold !== null) releaseReplayRecordingStop(hold)
                  },
                )
                .finally(() => { void refreshUser() })
            }}
            aria-label="Session recording"
          />
        </div>
        <p className="text-xs text-muted-foreground">
          {objected
            ? 'Recording is currently stopped for your account.'
            : 'Recording is currently active for your account.'}
        </p>
        {updatePreferences.isError ? (
          <p role="alert" className="text-xs text-destructive">
            We couldn't save that change. Please try again.
          </p>
        ) : null}
      </div>

      <AnalyticsObjection
        userId={userId}
        objected={Boolean(preferences?.analyticsSuppressedAt)}
        refreshUser={refreshUser}
      />
    </div>
  )
}

/**
 * KAN-904 — the member's self-service objection to product analytics.
 *
 * Writes the existing KAN-519 suppression (`user_preferences
 * .analytics_suppressed_at`) and stores nothing else. That one column is what
 * every enforcement point already reads:
 *
 *  - `/me` → `analyticsSuppressed` → `useAnalyticsIdentity` → the client
 *    chokepoint (`capture`, `identifyUser`, `before_send` all drop);
 *  - server-side capture (`readAnalyticsSuppressionDecision` in auth.ts);
 *  - Admin's usage view, which labels the member "opted out" (KAN-899 §7.1,
 *    B3 option B). Sign-ins are still recorded, and the copy says so.
 *
 * The switch shows collection: ON while collecting, OFF once the member has
 * objected (F51 polarity, see `PrivacySection`).
 *
 * Ordering is asymmetric on purpose:
 *  - objecting (switching OFF) stops capture in THIS tab before the request
 *    leaves, so nothing is captured during the round-trip;
 *  - withdrawing (switching ON) never resumes capture on the client's say-so. Capture resumes
 *    only when the refreshed `/me` says the server cleared the suppression,
 *    the same order the `/me` gate uses at boot.
 *
 * `/me` is re-read on SETTLED. After a failed save, capture and the switch both
 * fall back to what the server holds, and the member is told the save failed.
 *
 * Deliberately not `posthog.opt_out_capturing()`: posthog-js persists that to
 * localStorage, which the privacy notice says analytics never does, and a
 * browser-level flag would follow the next person who signs in on the device.
 */
function AnalyticsObjection({
  userId,
  objected,
  refreshUser,
}: {
  userId: string
  objected: boolean
  refreshUser: () => Promise<void>
}) {
  const setObjection = useSetAnalyticsObjection()

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-medium">Product analytics</p>
          <p className="text-xs text-muted-foreground">
            We record when you use parts of IntelHub, such as opening a story or
            running a search, so we can fix what is confusing and build what
            gets used. We don't record the text you type or the titles of what
            you read.
          </p>
          <p className="text-xs text-muted-foreground">
            Turn this off whenever you want. We stop collecting analytics for
            your account straight away, but we still record when you sign in.
          </p>
        </div>
        <Switch
          // ON = collection is on. OFF = the member has objected.
          checked={!objected}
          disabled={!userId}
          onCheckedChange={(collectionOn) => {
            const objecting = !collectionOn
            if (objecting) setAnalyticsSuppressed(true)
            setObjection.mutate(
              { userId, objected: objecting },
              { onSettled: () => { void refreshUser() } },
            )
          }}
          aria-label="Product analytics"
        />
      </div>
      <p className="text-xs text-muted-foreground">
        {objected
          ? 'Analytics collection is stopped for your account.'
          : 'Analytics collection is on for your account.'}
      </p>
      {setObjection.isError ? (
        <p role="alert" className="text-xs text-destructive">
          We couldn't save that change. Please try again.
        </p>
      ) : null}
    </div>
  )
}
